/*
 * Copyright (c) 2011 Macrofocus GmbH. All Rights Reserved.
 */
package org.mkui.coloring.implementation

import com.macrofocus.common.collection.CollectionFactory.copyOnWriteArrayList
import com.macrofocus.common.collection.WeakReference
import org.mkui.coloring.Coloring
import org.mkui.coloring.ColoringListener

/**
 * This class provides a skeletal implementation of the Coloring interface to minimize the effort required to implement
 * this interface.
 */
abstract class AbstractColoring<E> : Coloring<E> {
    private val listeners: MutableList<ColoringListener<E>>

    override fun addColoringListener(listener: ColoringListener<E>) {
        listeners.add(listener)
    }

    override fun addWeakColoringListener(listener: ColoringListener<E>) {
        listeners.add(WeakColoringListener(listener))
    }

    override fun removeColoringListener(listener: ColoringListener<E>) {
        if (listener is WeakColoringListener) {
            val removed = listeners.remove(listener)
        } else {
            var toRemove: ColoringListener<E>? = null
            for (filterListener in listeners) {
                var comparable: ColoringListener<E>?
                comparable = if (filterListener is WeakColoringListener) {
                    filterListener.reference
                } else {
                    filterListener
                }
                if (listener == comparable) {
                    toRemove = filterListener
                }
            }
            if (toRemove != null) {
                val removed = listeners.remove(toRemove)
            }
        }
    }

    override fun removeColoringListeners() {
        listeners.clear()
    }

    protected fun notifyColoringChanged(event: ColoringEvent<E>) {
        for (listener in listeners) {
            listener.coloringChanged(event)
        }
    }

    private inner class WeakColoringListener(listener: ColoringListener<E>) : ColoringListener<E> {
        private val l_ref: WeakReference<ColoringListener<E>>

        val reference: ColoringListener<E>?
            get() = l_ref.get()

        override fun coloringChanged(event: ColoringEvent<E>) {
            val l: ColoringListener<E>? = reference
            if (l != null) {
                l.coloringChanged(event)
            } else {
                removeColoringListener(this as ColoringListener<E>)
            }
        }

        init {
            l_ref = WeakReference(listener)
        }
    }

    init {
        listeners = copyOnWriteArrayList()
    }
}